import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import {
    loadContractConstants,
    setCurrentlySelectedParticipant
} from '../../../store/participantService';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { hasRole } from '../../../utils/userRoles';
import { conductAppointmentRoles, group1 } from '../utils/calendarUtils';

import AttendanceRow from './AttendanceRow';

import app from '../../../app.module.css';
import styles from '../styles/startAppointmentButton.module.css';

const StartAppointmentSlotsRows = ({
    participants,
    appointment,
    disabled,
    isUpdateDisabled,
    hasParticipantAccess = {},
    onPanelClose,
    statuses,
    isAuditable,
    onSearchAuditItems
}) => {
    // HOOKS
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [isPONumberRequired, setIsPONumberRequired] = useState(false);
    const [isNavPTRequestSent, setIsNavPTRequestSent] = useState(false);
    const [isNavStartRequestSent, setIsNavStartRequestSent] = useState(false);
    const [isNavUpdateRequestSent, setIsNavUpdateRequestSent] = useState(false);
    const [isWarning, setIsWarning] = useState(false);
    const [participant, setParticipant] = useState({});
    const [hasGroup1Role, setHasGroup1Role] = useState(false);
    const [hasEditOnClosedRole, setHasEditOnClosedRole] = useState(false);
    const [hasConductAppointmentRoles, setHasConductAppointmentRoles] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // USE EFFECTS
    useEffect(() => {
        setHasGroup1Role(hasRole(group1, roles));
        setHasEditOnClosedRole(hasRole(EDIT_ON_CLOSED_ROLES, roles));
        setHasConductAppointmentRoles(hasRole(conductAppointmentRoles, roles));
    }, [roles]);

    useEffect(() => {
        setRows(participants);
        if (participants.length < 1) return;
        if (!contractConstants || contractConstants.contractId !== participants[0].contractId)
            participant?.contractId && dispatch(loadContractConstants(participants[0].contractId));
    }, [participants]);

    useEffect(() => {
        if (!contractConstants || contractConstants.contractId !== participants[0]?.contractId)
            return;
        setIsPONumberRequired(contractConstants.requirePoNumberBeforeAppointmentInitialAttended);
    }, [contractConstants, participants]);

    useEffect(() => {
        if (
            currentParticipant?.ptCode === participant.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        ) {
            if (isNavPTRequestSent) onPTNav();
            if (isNavStartRequestSent) onAppointmentNav('start');
            if (isNavUpdateRequestSent) onAppointmentNav('update');
        }
    }, [
        isNavPTRequestSent,
        isNavStartRequestSent,
        isNavUpdateRequestSent,
        contractDetailsById,
        servicesByContractId,
        currentParticipant
    ]);

    // FNS
    const onPTNav = () => {
        onPanelClose();
        navigate('/edit_participant');
    };

    const onAppointmentNav = (action) => {
        const appointmentAttendanceId = appointment.attendances.find(
            (el) => el.participantId === participant.id
        )?.id;
        if (disabled || !appointmentAttendanceId) return;
        onPanelClose();
        navigate('/appointments', {
            state: {
                appointment,
                appointmentAttendanceId,
                showWarning: isWarning,
                returnPath: location.pathname,
                action,
                participantId: currentParticipant?.id
            }
        });
    };

    const onAction = (participant) => {
        setParticipant(participant);
        dispatch(setCurrentlySelectedParticipant(participant));
        if (!(participant.contractId in contractDetailsById)) {
            dispatch(loadContractDetailsForContractId(participant.contractId));
        }
        if (!(participant.contractId in servicesByContractId)) {
            dispatch(loadServiceDetailsForContractId(participant.contractId));
        }
    };

    // EVENT HANDLERS
    const onStartAppointment = async (showWarning, participant) => {
        setIsNavStartRequestSent(true);
        setIsWarning(showWarning);
        await onAction(participant);
    };

    const onUpdateAppointment = async (showWarning, participant) => {
        setIsNavUpdateRequestSent(true);
        setIsWarning(showWarning);
        await onAction(participant);
    };

    const onNav = async (selectedParticipant) => {
        setIsNavPTRequestSent(true);
        if (selectedParticipant.hidden) return;
        await onAction(selectedParticipant);
    };

    // RENDER
    return rows.length < 1 ? null : (
        <div
            className={`${styles.startAppointmentButtons} ${app.fadein}`}
            data-testid={'startSlotAppointmentRows'}>
            {participants.map((el) => (
                <AttendanceRow
                    key={el.id}
                    isDisabled={
                        disabled ||
                        el.hidden ||
                        (statuses[el.id] && statuses[el.id] !== 'Not known')
                    }
                    isUpdateDisabled={isUpdateDisabled || el.hidden}
                    hasParticipantAccess={hasParticipantAccess[el?.id] || false}
                    hasGroup1Role={hasGroup1Role}
                    hasEditOnClosedRole={hasEditOnClosedRole}
                    canConductAppointment={
                        (hasParticipantAccess[el?.id] && hasConductAppointmentRoles) || false
                    }
                    isAuditable={isAuditable}
                    onSearchAuditItems={() =>
                        onSearchAuditItems(
                            'attendance',
                            `${el?.firstName || ''} ${el?.lastName || ''} - Attendance`,
                            el.id
                        )
                    }
                    attendanceStatus={statuses[el.id]}
                    appointmentType={appointment?.type.toLowerCase()}
                    isPONumberRequired={isPONumberRequired}
                    participant={el}
                    onNav={() => onNav(el)}
                    onStartAppointment={onStartAppointment}
                    onUpdateAppointment={onUpdateAppointment}
                />
            ))}
        </div>
    );
};

StartAppointmentSlotsRows.propTypes = {
    participants: PropTypes.arrayOf(PropTypes.object),
    appointment: PropTypes.object,
    disabled: PropTypes.bool,
    isUpdateDisabled: PropTypes.bool,
    hasParticipantAccess: PropTypes.object,
    onPanelClose: PropTypes.func,
    statuses: PropTypes.object,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func
};

export default StartAppointmentSlotsRows;
